import { useEffect, useRef } from "react";

export type WsEvent =
  | { type: "ping" }
  | { type: "message:new"; threadId: string; from: string; subject: string; tier: string }
  | { type: "thread:updated"; threadId: string }
  | { type: "ai:draft:delta"; draftId: string; chunk: string }
  | { type: "ai:draft:done"; draftId: string }
  | { type: "case:new"; caseId: string }
  | { type: "case:updated"; caseId: string };

type Listener = (ev: WsEvent) => void;

function wsUrl(): string {
  if (typeof window === "undefined") return "";
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const base = import.meta.env.VITE_API_BASE;
  // 绝对地址 (直连其它后端): VITE_API_BASE=https://api.example.com → wss://api.example.com/ws
  if (base && /^https?:\/\//.test(base)) {
    const u = new URL(base);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    return `${u.origin}/ws`;
  }
  // 默认走 /api/ws —— dev: Vite proxy / prod: SWA Linked Backend
  return `${proto}//${window.location.host}/api/ws`;
}

export function useWs(listener: Listener): void {
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let retry = 0;
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (stopped) return;
      ws = new WebSocket(wsUrl());
      ws.onopen = () => {
        retry = 0;
      };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data) as WsEvent;
          listenerRef.current(data);
        } catch {
          // ignore
        }
      };
      ws.onclose = () => {
        if (stopped) return;
        const delay = Math.min(1000 * 2 ** retry, 15_000);
        retry += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);
}
